import React, { startTransition, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { get, isEmpty, isEqual, noop, isArray, isNull, omit } from 'lodash-es';
import { Container } from 'reactstrap';
/* Utils */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { vdpLinkBuilder } from 'site-modules/shared/utils/vdp-link-constructor';
import {
  createLinkForStep,
  setFinancingContainerFields,
  postValidateInfo,
  createLookUpLink,
  verifyOffers,
  createErrorPath,
  isE2ETest,
} from 'client/site-modules/financing/utils/financing-utils';
import { getSelectedOffer, isCapOneProvider } from 'client/site-modules/shared/utils/financing/financing';
import { venomHistory } from 'client/utils/history/venom-history';
import { scrollToTop } from 'client/utils/scroll';
import { FinancingStorage } from 'client/site-modules/financing/utils/storage';
import { updateCreativeId } from 'site-modules/financing/utils/update-creative-id';
import { getQuery } from 'client/utils/location';
import { ApiMetrics, withMetrics } from 'client/utils/metrics-hoc';
import { postFinancingOffers } from 'site-modules/financing/utils/post-financing';
/* Tracking */
import { EngagementHandler } from 'client/engagement-handlers';
import { FinancingEngagementHandler } from 'client/engagement-handlers/financing-engagement-handler/financing-engagement-handler';
import { InventoryEngagementHandler } from 'client/engagement-handlers/inventory-engagement-handler/inventory-engagement-handler';
import { TrackingHandler } from 'client/tracking/handler';
import { ACTIONS } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { EventToolbox } from 'client/utils/event-toolbox';
import { FINANCING_EVENTS } from 'site-modules/shared/constants/financing/financing-events';
import { TrackingMap } from 'client/tracking/maps/financing/financing';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
import { FinancingModel, PATHS } from 'client/data/models/financing';
import { DealerInfoEntities, DealerInfoModel, getDealerInfoPath } from 'client/data/models/dealer-info';
/* Constants */
import { Location } from 'client/site-modules/financing/constants/models';
import { STANDALONE_STEPS } from 'client/site-modules/financing/constants/steps';
import {
  BASICS_STEP,
  LOOK_UP_STEP,
  PERSONAL_STEP,
  REVIEW_STEP,
  CONFIRMATION_STEP,
  STEPS_MAP,
  INTRO_STEP,
  ERROR_STEP,
  PAYMENTS_STEP,
  REQUEST_STEP,
} from 'site-modules/shared/constants/financing/steps';
import {
  JOINT,
  USED_PREQUALIFICATION_FINANCING_LEAD,
} from 'site-modules/shared/constants/financing/financing-constants';
import { CONSUMER_EMAIL_STATUSES, ERROR_STATUS } from 'site-modules/shared/constants/financing/financing';
import { ERRORS_MAP } from 'client/site-modules/financing/constants/error-steps';
import { STORAGE_KEYS } from 'site-modules/financing/constants/financing';
/* Components */
import { StepsNavigation } from 'client/site-modules/financing/components/steps-navigation/steps-navigation';
import { PrequalifiedCta } from 'client/site-modules/financing/components/financing-container/components/prequalified-cta/prequalified-cta';
import { WithRejectionScreen } from 'site-modules/financing/components/financing-container/components/with-rejection-screen/with-rejection-screen';
import { FinancingVehicleInfo } from 'site-modules/financing/components/financing-container/components/financing-vehicle_info/financing-vehicle_info';
import { FinancingResults } from 'site-modules/financing/components/financing-container/components/financing_results/financing_results';
import { DisclaimerInfo } from 'client/site-modules/financing/components/financing-container/components/disclaimer-info/disclaimer-info';
import { ConfirmationHeader } from 'site-modules/financing/components/financing-container/components/confirmation-header/confirmation-header';
import { BackButton } from 'site-modules/financing/components/shared/back-button/back-button';
import { NextButton } from 'site-modules/financing/components/shared/next-button/next-button';
/* HOC */
import { connectedFinancingApplication } from 'client/site-modules/financing/components/calculate-financing/calculate-financing';

import './financing-container.scss';

EngagementHandler.register(FinancingEngagementHandler);
EngagementHandler.register(InventoryEngagementHandler);

export function FinancingContainerUI({
  location,
  activeStep,
  children = null,
  vin = null,
  setModelValue,
  vehicle = {},
  isMobile = false,
  isJoint = false,
  isAbandonFlow = false,
  submitLead,
  calculatorSelections = {},
  errorStatus = '',
  clearFinancingPath = noop,
  sendConsumerEmail,
  visitedSteps = [],
  isProdEnv = false,
  dealerInfo = {},
  sendPreQualTermEmail = noop,
  isEligibleVIN = true,
  isErrorActiveStep = false,
  debugFinancingAPI = false,
  isPreVinFlow = false,
  isAllowBypassApiGateway = false,
  configOptions = {},
  apiMetrics,
  xHeaders = {},
  visitorId = null,
  prequalificationData = null,
}) {
  const wrapperRef = useRef(null);
  const isSelectLookupRates = useRef(false);

  const [financingOffers, setFinancingOffers] = useState(null);
  const [enabledNextBtn, setEnabledNextBtn] = useState(activeStep === INTRO_STEP);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFeedbackScreen, setIsLoadingFeedbackScreen] = useState(false);
  const [isLoadingPayments, setIsLoadingPayments] = useState(activeStep === PAYMENTS_STEP);
  const [qualifiedStatus, setQualifiedStatus] = useState('');
  const [isJointState, setIsJointState] = useState(isJoint);
  const [validationInfo, setValidationInfo] = useState({});
  const [savedOffers, setSavedOffers] = useState([]);
  const [formData, setFormData] = useState({});
  const [counters, setCounters] = useState({
    showMessage: false,
    type: '',
    text: [],
    counterNames: [],
    messages: [],
    isOffersAvailable: false,
  });

  useEffect(
    () => {
      if (!isNull(financingOffers) && isEmpty(savedOffers) && isLoadingPayments) {
        startTransition(() => setIsLoadingPayments(false));
      }
      if (isArray(financingOffers) && !isEmpty(financingOffers) && !isEqual(savedOffers, financingOffers)) {
        startTransition(() => {
          setCounters(verifyOffers(financingOffers));
          setSavedOffers(financingOffers);
          setIsLoadingPayments(false);
        });
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [financingOffers]
  );

  const { pageName, venomVersion } = apiMetrics;
  const isE2E = isE2ETest(location);
  const isIntro = activeStep === INTRO_STEP;
  const step = STEPS_MAP[activeStep];
  const errorStep = ERRORS_MAP[errorStatus];
  const isCapOne = isCapOneProvider(vehicle);
  const isErrorStep = activeStep === ERROR_STEP || isErrorActiveStep;
  const headerClass = {
    true: 'size-30 mb-2',
    false: 'heading-4 mb-0_5',
  }[isIntro];

  const [lastCompletedStep, offersFromStorage] = useMemo(
    () => [
      FinancingStorage.getValueFromSessionStorage(`${vin}.lastCompletedStep`),
      FinancingStorage.getValueFromSessionStorage(`${vin}.${STORAGE_KEYS.FINANCING_OFFERS}`),
    ],
    [vin]
  );

  const showErrorPage = async error => {
    const shouldSendEmail = CONSUMER_EMAIL_STATUSES.includes(error);
    if (isPreVinFlow) {
      await setModelValue(PATHS.PRE_VIN_ERROR_STATUS, FinancingModel, error);
    } else {
      await setModelValue(PATHS.ERROR_STATUS({ vin }), FinancingModel, error);
    }

    if (shouldSendEmail) {
      await sendConsumerEmail(error);
    }
    const redirectLink = createErrorPath(activeStep, location);
    venomHistory.push(redirectLink);
  };

  const showFinancingVehicleInfoError = get(errorStep, 'showFinancingVehicleInfoError', false) && isErrorStep;

  const [
    showDisclaimer,
    showFinancingVehicleInfo,
    showFinancingResults,
    showNavigation,
    showHeader,
    showBackBtn,
    showPrequalifyCta,
    showNextBtn,
    btnText,
    showConfirmationHeader,
    header,
    mobileHeader,
    subheader,
    order,
  ] = useMemo(
    () => [
      get(step, 'showDisclaimer', false),
      get(step, 'showFinancingVehicleInfo', showFinancingVehicleInfoError),
      get(step, 'showFinancingResults', false),
      get(step, 'showNavigation', false),
      get(step, 'showHeader', false),
      get(step, 'showBackBtn', false),
      get(step, 'showPrequalifyCta', false),
      get(step, 'showNextBtn', false),
      get(step, 'btnText', ''),
      get(step, 'showConfirmationHeader', false),
      get(step, 'header'),
      get(step, 'mobileHeader'),
      get(step, 'subheader'),
      get(step, 'order'),
    ],
    [step]
  );

  const isAllStepsSkipped = !lastCompletedStep;
  const lastVisitedStep = visitedSteps[visitedSteps.length - 1];
  const lastCompletedStepOrder = get(STEPS_MAP[lastVisitedStep], 'order');
  const isBackState = activeStep !== lastVisitedStep && order < lastCompletedStepOrder;
  const isLookUp = activeStep === LOOK_UP_STEP;
  const isBasics = activeStep === BASICS_STEP;
  const isRequestStep = activeStep === REQUEST_STEP; // TODO: Remove when shop-4713-prequal2 test is cleaned up
  const apiOptions = { ...apiMetrics, xHeaders, visitorId };
  const isStandaloneStep = STANDALONE_STEPS.includes(activeStep);

  const getFinancingOffers = useCallback(async () => {
    if (isStandaloneStep && isCapOne) {
      await postFinancingOffers({
        data: {
          prequalificationData,
          paymentData: { calculatorSelections, vehicle },
          additionalData: { isE2E },
        },
        apiMetrics: apiOptions,
        options: configOptions,
        handlers: { showErrorPage, setFinancingOffers, setModelValue },
      });
    }
  }, [isCapOne, isStandaloneStep, prequalificationData, location]);

  const creativeId = useMemo(() => {
    if (isIntro) return 'edm-entry-vin-overview-financing-intro';

    if (isRequestStep) {
      return USED_PREQUALIFICATION_FINANCING_LEAD;
    }

    return 'edm-entry-vin-overview-financing-vehicle_info';
  }, [isRequestStep, isIntro]);

  useEffect(
    () => {
      TrackingHandler.useMap(TrackingMap);

      if (!isEligibleVIN && !isErrorStep && !isPreVinFlow && !isRequestStep) {
        showErrorPage(ERROR_STATUS.INELIGIBLE);
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    []
  );

  useEffect(() => {
    if (isStandaloneStep && isCapOne && (prequalificationData || isE2E)) {
      if (!isEmpty(offersFromStorage)) {
        startTransition(() => setFinancingOffers(offersFromStorage));
      } else {
        getFinancingOffers();
      }
    }
  }, [activeStep, vehicle, offersFromStorage, prequalificationData]);

  useEffect(() => {
    if (isSelectLookupRates.current) {
      TrackingHandler.fireEvent(
        'lookup_rates_status',
        {},
        {
          action_cause: 'button_click',
          creative_id: updateCreativeId({ isMatchingVIN: isCapOne, trackingParent: 'financing_results' }),
          value: qualifiedStatus,
        }
      );
      isSelectLookupRates.current = false;
    }
  }, [isCapOne, qualifiedStatus]);

  useEffect(() => {
    if (showFinancingVehicleInfo && !isPreVinFlow && vehicle) {
      EventToolbox.fireCustomEvent(ACTIONS.INVENTORY_LIST_VIEW, {
        inventory: [vehicle],
        subaction_name: 'prequalify',
        creativeId,
        ts: Date.now(),
      });
    }
  }, [showFinancingVehicleInfo, vehicle, creativeId]);

  const { make, model, year } = get(vehicle, 'vehicleInfo.styleInfo', {});
  let vdpLink;

  if (!isPreVinFlow) {
    vdpLink = vdpLinkBuilder({ make, model, year, vin }, { ...omit(getQuery(location), 'debugFinancingAPI') });
  }

  const setContainerFields = useCallback(
    async fields => {
      setIsLoading(true);
      await setFinancingContainerFields(fields, activeStep);
    },
    [activeStep]
  );

  const changePageState = useCallback(async type => {
    const isJointType = type && type.toLowerCase() === JOINT;
    setIsJointState(isJointType);
  }, []);

  const redirectPage = useCallback(
    (isPrev = false) => {
      const redirectLink = createLinkForStep({
        location,
        activeStep,
        step,
        isPrev,
        isJointState,
        isCapOne,
        isPreVinFlow,
      });
      if (vin) {
        setModelValue(PATHS.LAST_COMPLETED_STEP({ vin }), FinancingModel, activeStep);
      }
      venomHistory.push(redirectLink);
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [activeStep, isJointState]
  );

  const onSubmit = async submittedFields => {
    setIsLoadingFeedbackScreen(true);
    scrollToTop();

    if (isLookUp) {
      isSelectLookupRates.current = true;
      await setContainerFields(submittedFields ?? formData);

      TrackingHandler.fireEvent(
        'lookup_next_click',
        {},
        {
          action_cause: 'button_click',
          creative_id: 'spinner',
        }
      );
    }
  };

  const getEmailPhoneValidationStatus = async currentStep => {
    if (currentStep !== PERSONAL_STEP) return true;
    setEnabledNextBtn(false);
    setIsLoading(true);
    const { email, phoneNumber, emailPrimary, phoneNumberPrimary } = formData;
    const response = await postValidateInfo(pageName, venomVersion, isJoint, {
      email,
      phoneNumber,
      emailPrimary,
      phoneNumberPrimary,
    });

    const isError = get(response, 'isError', false);

    if (isError) {
      // if there is an error from the validation api, then we should not block
      // the users and continue with the form submission (ie. execute redirectPage() as
      // if there is no error)

      return true;
    }

    const { validationResult, formValid } = response;

    setValidationInfo(validationResult);

    return formValid;
  };

  const goToNextScreen = useCallback(async () => {
    const formValid = await getEmailPhoneValidationStatus(activeStep);
    if (!formValid) {
      setEnabledNextBtn(true);
      setIsLoading(false);
      return;
    }

    if (activeStep !== INTRO_STEP) {
      setEnabledNextBtn(false);
    }

    await setContainerFields(formData);
    setIsLoadingFeedbackScreen(false);

    EventToolbox.fireCustomEvent(FINANCING_EVENTS.NEXT_STEP_BTN, {
      activeStep,
      isJoint: isJointState,
      isMatchingVIN: isCapOne,
      isPreVinFlow,
    });

    redirectPage();
  }, [activeStep, isJointState, formData]);

  const goToPrevScreen = useCallback(
    () => {
      EventToolbox.fireCustomEvent(FINANCING_EVENTS.BACK_BTN, {
        activeStep,
        isJoint: isJointState,
        isMatchingVIN: isCapOne,
        isPreVinFlow,
      });
      redirectPage(true);
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [activeStep, isJointState]
  );

  const sendToDealer = async e => {
    const targetEl = e.target;
    const offer = getSelectedOffer(financingOffers, calculatorSelections);
    const res = await submitLead(targetEl, { financingOffer: offer, calculatorSelections });

    if (res && res.status === 'OK') {
      redirectPage();
    }
  };

  if ((!isEligibleVIN && !isErrorStep && !isPreVinFlow && !isRequestStep) || (isErrorStep && !errorStatus)) {
    return null;
  }

  return (
    <main id="main-content" tabIndex={-1} className="financing-page mb-2">
      {showFinancingVehicleInfo && isCapOne && !isPreVinFlow && (
        <FinancingVehicleInfo
          isMobile={isMobile}
          isLoadingFeedbackScreen={isLoadingFeedbackScreen}
          vdpLink={vdpLink}
          vehicle={vehicle}
          vin={vin}
          header={header}
        />
      )}
      {showFinancingResults && isCapOne && !isPreVinFlow && (
        <FinancingResults
          isMobile={isMobile}
          vdpLink={vdpLink}
          vehicle={vehicle}
          vin={vin}
          header={header}
          isMatchingVIN={isCapOne}
          isOffersAvailable={counters.isOffersAvailable}
          isLoading={isLoadingPayments}
          financingOffers={financingOffers}
          calculatorSelections={calculatorSelections}
        />
      )}
      {showConfirmationHeader && isCapOne && !isPreVinFlow && <ConfirmationHeader />}
      <div ref={wrapperRef}>
        <Container
          className={classNames('financing-container', {
            'standalone px-1_25 px-md-0': STANDALONE_STEPS.includes(activeStep),
          })}
        >
          <WithRejectionScreen isAbandonFlow={isAbandonFlow} wrapperRef={wrapperRef}>
            {showNavigation && !isLoadingFeedbackScreen && (
              <StepsNavigation
                activeStep={activeStep}
                lastCompletedStep={lastCompletedStep}
                location={location}
                enabledNextBtn={enabledNextBtn}
                isMobile={isMobile}
                goToNextScreen={goToNextScreen}
                goToPrevScreen={goToPrevScreen}
                showBackBtn={showBackBtn}
                vin={vin}
                setModelValue={setModelValue}
                isMatchingVIN={isCapOne}
                isPreVinFlow={isPreVinFlow}
              />
            )}

            <div
              className={classNames({
                'mt-2 mt-md-3_5': !(activeStep === CONFIRMATION_STEP),
                'px-md-2': !(isLookUp || STANDALONE_STEPS.includes(activeStep) || isIntro || activeStep === ERROR_STEP),
                'pos-r confirmation-container': activeStep === CONFIRMATION_STEP && isCapOne,
              })}
            >
              {showHeader && !isLoadingFeedbackScreen && (
                <>
                  {header && (
                    <h1 className={classNames('fw-bold text-center', headerClass)}>
                      {isMobile && mobileHeader ? mobileHeader : header}
                    </h1>
                  )}
                  {subheader && (
                    <div className={classNames('subheader', isIntro ? 'size-20 intro mb-2' : 'text-center')}>
                      {subheader}
                    </div>
                  )}
                </>
              )}

              {/* eslint-disable-next-line react-hooks/refs -- dynamic child may carry a ref; this component does not read it during render */}
              {React.cloneElement(children, {
                currentStep: step,
                changePageState,
                enableNextBtn: setEnabledNextBtn,
                goToNextScreen,
                isJoint,
                vdpLink,
                vehicle,
                vin,
                validationInfo,
                isAllStepsSkipped,
                sendToDealer,
                isBackState,
                financingOffers,
                calculatorSelections,
                isMobile,
                isMatchingVIN: isCapOne,
                isEligibleVIN, // TODO: Remove when shop-4713-prequal2 test is cleaned up
                counters,
                isProdEnv,
                dealerInfo,
                sendPreQualTermEmail,
                errorStatus,
                clearFinancingPath,
                isLoadingPayments,
                enabledNextBtn,
                onSubmit,
                isLoadingFeedbackScreen,
                redirectPage,
                showErrorPage,
                sendConsumerEmail,
                setIsLoadingPayments,
                setQualifiedStatus,
                goToPrevScreen,
                debugFinancingAPI,
                isLoading,
                setFormData,
                formData,
                isPreVinFlow,
                isAllowBypassApiGateway,
                configOptions,
                apiMetrics: { ...apiMetrics, xHeaders, visitorId },
                setFinancingOffers,
                setModelValue,
              })}
            </div>
            {showNextBtn && !isErrorStep && (
              <div
                className={classNames('d-flex buttons-container mx-auto px-0_5 px-0_5', {
                  'justify-content-between': showBackBtn,
                  'justify-content-center': !showBackBtn,
                })}
              >
                {showBackBtn && (
                  <BackButton
                    onClick={goToPrevScreen}
                    className={activeStep === REVIEW_STEP ? 'align-self-baseline' : ''}
                  />
                )}
                <div>
                  <NextButton
                    onClick={goToNextScreen}
                    enabledNextBtn={enabledNextBtn}
                    isFullWidth={isMobile && !showBackBtn}
                    isLoading={isLoading}
                    btnText={btnText}
                  />
                </div>
              </div>
            )}
            {activeStep === PERSONAL_STEP && (
              <div className="mb-1_25 mt-2 size-12 px-0_5 mx-auto disclaimer" id="email-agreement">
                † When you provide your email address to Participating Lenders, they may use it to send important
                information about your pre-qualification request.
              </div>
            )}
            {showPrequalifyCta && (
              <PrequalifiedCta
                link={createLookUpLink(location, activeStep, true, isJointState)}
                isBasicView={[INTRO_STEP, BASICS_STEP].includes(activeStep)}
                isMobile={isMobile}
                activeStep={activeStep}
                isMatchingVIN={isCapOne}
                isPreVinFlow={isPreVinFlow}
              />
            )}
          </WithRejectionScreen>
        </Container>
      </div>
      {showDisclaimer && <DisclaimerInfo isBasics={isBasics} />}
    </main>
  );
}

FinancingContainerUI.propTypes = {
  apiMetrics: ApiMetrics.isRequired,
  location: Location.isRequired,
  activeStep: PropTypes.string.isRequired,
  children: PropTypes.node,
  setModelValue: PropTypes.func.isRequired,
  vin: PropTypes.string,
  vehicle: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool,
  isJoint: PropTypes.bool,
  isAbandonFlow: PropTypes.bool,
  sendConsumerEmail: PropTypes.func.isRequired,
  submitLead: PropTypes.func.isRequired,
  calculatorSelections: PropTypes.shape({}),
  errorStatus: PropTypes.string,
  clearFinancingPath: PropTypes.func,
  visitedSteps: PropTypes.arrayOf(PropTypes.string),
  isProdEnv: PropTypes.bool,
  dealerInfo: DealerInfoEntities.DealerInfo,
  sendPreQualTermEmail: PropTypes.func,
  isEligibleVIN: PropTypes.bool,
  isErrorActiveStep: PropTypes.bool,
  debugFinancingAPI: PropTypes.bool,
  isPreVinFlow: PropTypes.bool,
  isAllowBypassApiGateway: PropTypes.bool,
  configOptions: PropTypes.shape({}),
  xHeaders: PropTypes.shape({}),
  visitorId: PropTypes.string,
  prequalificationData: PropTypes.string,
};

export const stateToPropsConfig = {
  prequalificationData: bindToPath(PATHS.PREQUALIFICATION_DATA, FinancingModel),
  calculatorSelections: bindToPath(({ vin }) => PATHS.CALCULATOR_SELECTIONS({ vin }), FinancingModel),
  visitedSteps: bindToPath(({ vin }) => PATHS.VISITED_STEPS({ vin }), FinancingModel),
  dealerInfo: bindToPath(({ vehicle }) => getDealerInfoPath(vehicle), DealerInfoModel),
  errorStatus: bindToPath(
    ({ vin, isPreVinFlow }) => (isPreVinFlow ? PATHS.PRE_VIN_ERROR_STATUS : PATHS.ERROR_STATUS({ vin })),
    FinancingModel
  ),
};

export const mapStateToProps = state => ({
  xHeaders: state.xHeaders,
  visitorId: state.visitor?.id,
  isMobile: state.mobile,
  isAbandonFlow: get(state.featureFlags, 'capOne-isAbandonFlow'),
  isProdEnv: state.venomEnv === 'prod',
});

export const FinancingContainer = connectedFinancingApplication(
  connect(mapStateToProps)(connectToModel(withMetrics(FinancingContainerUI, true), stateToPropsConfig))
);
